import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { DifficultyStatItem } from '../../types/admin';

interface Props {
  data: Record<string, DifficultyStatItem>;
}

const COLORS: Record<string, string> = {
  easy: '#22C55E',
  medium: '#F59E0B',
  hard: '#EF4444',
};

export default function DifficultyBreakdown({ data }: Props) {
  const { t } = useTranslation();
  const chartData = Object.entries(data).map(([level, stats]) => ({
    name: level.charAt(0).toUpperCase() + level.slice(1),
    accuracy: stats.accuracy,
    answered: stats.answered,
    correct: stats.correct,
    level,
  }));

  if (chartData.length === 0) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">{t('dashboard.difficulty.empty')}</p>;
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-white/10" opacity={0.5} />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} className="text-slate-600 dark:text-slate-400" />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} className="text-slate-500 dark:text-slate-400" />
          <Tooltip
            contentStyle={{ backgroundColor: '#141C2B', border: '1px solid rgba(255,255,255,0.13)', borderRadius: '12px', fontSize: 12, color: '#EEF2F8' }}
            formatter={(value, _, props) => {
              const p = props?.payload as { correct?: number; answered?: number } | undefined;
              return [t('dashboard.difficulty.tooltipFormat', { percent: value, correct: p?.correct ?? 0, answered: p?.answered ?? 0 }), t('dashboard.difficulty.tooltipLabel')];
            }}
          />
          <Bar dataKey="accuracy" radius={[4, 4, 0, 0]}>
            {chartData.map((entry) => (
              <Cell key={entry.level} fill={COLORS[entry.level] ?? '#3B82F6'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
